import type { DivisaoDaLei } from '@titularis/api-client';

/**
 * A estrutura da lei, plana no servidor e em árvore na tela.
 *
 * **O servidor devolve a lista em ordem de leitura, e não aninhada**, porque é
 * assim que ela é: um `CAPÍTULO` não "contém" os artigos no banco, ele apenas
 * começa antes deles. Aninhar é decisão de apresentação, e mora aqui.
 */

/** A hierarquia da LC 95/1998, art. 10. Menor é mais raso. */
const PROFUNDIDADE: Record<string, number> = {
  parte: 1,
  livro: 2,
  titulo: 3,
  capitulo: 4,
  secao: 5,
  subsecao: 6,
};

export interface NoDaArvore {
  /** `4|CAPÍTULO II`, único dentro da lei e estável entre carregamentos. */
  chave: string;
  rotulo: string;
  titulo: string;
  pagina: number;
  filhos: NoDaArvore[];
}

/**
 * Aninha a lista pela profundidade do nível.
 *
 * **Pela profundidade, e não pela ordem de chegada**: a lei não é obrigada a
 * usar todos os níveis, e o Código Civil salta de `LIVRO` para `TÍTULO` em um
 * lugar e de `TÍTULO` direto para `SEÇÃO` em outro. Empilhar por ordem faria a
 * seção virar filha do capítulo errado; empilhar por profundidade acerta os
 * dois casos com a mesma regra.
 *
 * Nível desconhecido entra no fundo da pilha, e não é descartado: divisão que a
 * tela não sabe aninhar ainda é divisão que o aluno quer alcançar.
 */
export function arvoreDeSecoes(divisoes: DivisaoDaLei[]): NoDaArvore[] {
  const raiz: NoDaArvore[] = [];
  /** A cadeia aberta, do mais raso ao mais fundo. */
  const pilha: { profundidade: number; no: NoDaArvore }[] = [];

  for (const [i, divisao] of divisoes.entries()) {
    const profundidade = PROFUNDIDADE[divisao.nivel] ?? 99;
    const no: NoDaArvore = {
      // O índice entra na chave porque a mesma divisão se repete de verdade: o
      // Código Civil tem duas `Seção I - Da Usucapião`, a do imóvel e a do móvel.
      chave: `${i}|${divisao.rotulo}`,
      rotulo: divisao.rotulo,
      titulo: divisao.titulo,
      pagina: divisao.pagina,
      filhos: [],
    };

    while (pilha.length > 0 && pilha[pilha.length - 1]!.profundidade >= profundidade) {
      pilha.pop();
    }

    const pai = pilha[pilha.length - 1];
    if (pai) pai.no.filhos.push(no);
    else raiz.push(no);

    pilha.push({ profundidade, no });
  }

  return raiz;
}

/**
 * A página que o número digitado pede, dentro do que a lei tem.
 *
 * Devolve `null` para o que não é página: o campo aceita digitação livre, e
 * `irParaPagina(NaN)` põe `page=NaN` na URL - que sobrevive ao recarregamento e
 * deixa a lei em branco sem dizer por quê.
 */
export function paginaPedida(texto: string, total: number): number | null {
  const numero = Number(texto.trim());
  if (!Number.isInteger(numero) || numero < 1 || numero > total) return null;
  return numero;
}
